import { DEFAULT_LOCALE } from '@core/i18n';

import {
  type Blocker,
  type BlockerAction,
  type BlockerApi,
  type BlockerTone,
  type DayGreetingPeriod,
  type FacilityCycle,
  type FacilityCycleApi,
  type Invoice,
  type InvoiceApi,
} from './home.types';

export function mapInvoiceApiToInvoice(invoiceApi: InvoiceApi): Invoice {
  return {
    id: invoiceApi.id,
    patientId: invoiceApi.patient.id,
    patientName: `${invoiceApi.patient.first_name} ${invoiceApi.patient.last_name}`.trim(),
    dueDate: invoiceApi.due_date,
    created: invoiceApi.created,
    dueAmount: invoiceApi.due_amount ?? 0,
    status: invoiceApi.status,
    officeId: invoiceApi.office_id,
    officeName: invoiceApi.office_name,
    officeAddress: invoiceApi.office_address,
    paymentStatus: invoiceApi.payment_status,
    isFinanciallyResponsible: invoiceApi.is_financially_responsible,
  };
}

export function mapBlockerApiToBlocker(blockerApi: BlockerApi): Blocker {
  return {
    id: blockerApi.id,
    patientId: blockerApi.patient.id,
    patientName: `${blockerApi.patient.first_name} ${blockerApi.patient.last_name}`.trim(),
    stepName: blockerApi.step.name,
    stepOrder: blockerApi.step.order,
    description: blockerApi.description,
    created: blockerApi.created,
    activityId: blockerApi.activity.id,
    activityType: blockerApi.activity.type,
    resolveType: blockerApi.resolve_type,
    formSchema: blockerApi.form.schema,
  };
}

export function mapFacilityCycleApiToFacilityCycle(cycleApi: FacilityCycleApi): FacilityCycle {
  return {
    id: cycleApi.id,
    cycleBegin: cycleApi.cycle_begin,
    cycleEnd: cycleApi.cycle_end,
    cycleDelivery: cycleApi.cycle_delivery,
    censusCount: cycleApi.census_count ?? 0,
    changesCount: cycleApi.changes_count ?? 0,
    invoicesCount: cycleApi.invoices_count ?? 0,
    risksCount: cycleApi.risks_count ?? 0,
    checkedIn: cycleApi.checked_in,
    facilityName: cycleApi.facility_name,
  };
}

/**
 * Returns `previousValue` when it deep-equals `nextValue` (same reference kept);
 * otherwise returns `nextValue`. Supports empty arrays and `0`.
 */
export function keepPreviousIfEqual<T>(previousValue: T, nextValue: T): T {
  return Object.is(previousValue, nextValue) ||
    toComparableJsonString(previousValue) === toComparableJsonString(nextValue)
    ? previousValue
    : nextValue;
}

function toComparableJsonString(value: unknown): string {
  return JSON.stringify(value);
}

export function isDueDateToday(dueDate: string, now = new Date()): boolean {
  const due = parseLocalDateOnly(dueDate);
  if (!due) return false;

  return (
    due.getFullYear() === now.getFullYear() &&
    due.getMonth() === now.getMonth() &&
    due.getDate() === now.getDate()
  );
}

/**
 * Resolves morning | afternoon | evening from the device's local clock
 * (morning 5–11, afternoon 12–19, evening otherwise).
 */
export function resolveLocalDayGreetingPeriod(now = new Date()): DayGreetingPeriod {
  const hour = now.getHours();
  if (hour >= 5 && hour < 12) return 'morning';
  if (hour >= 12 && hour < 20) return 'afternoon';
  return 'evening';
}

/** Formats an amount as a localized currency string (default USD). */
export function formatAmountAsCurrency(
  amount: number,
  locale = DEFAULT_LOCALE,
  currency = 'USD',
): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
  }).format(amount);
}

export function resolveBlockerTone(resolveType: string): BlockerTone {
  return normalizeBlockerResolveType(resolveType) === 'needs_review' ? 'neutral' : 'urgent';
}

export function resolveBlockerStatusLabel(
  resolveType: string,
  translate: (key: string) => string,
): string {
  switch (normalizeBlockerResolveType(resolveType)) {
    case 'needs_information':
      return translate('home.blockers.status.needsInformation');
    case 'add_document':
      return translate('home.blockers.status.urgent');
    case 'needs_review':
      return translate('home.blockers.status.needsReview');
    default:
      return translate('home.blockers.status.needsAttention');
  }
}

export function resolveBlockerActions(
  resolveType: string,
  translate: (key: string) => string,
): readonly BlockerAction[] {
  switch (normalizeBlockerResolveType(resolveType)) {
    case 'add_document':
      return [{ kind: 'addDocument', label: translate('home.blockers.actions.addDocument') }];
    case 'needs_review':
      return [
        { kind: 'reject', label: translate('home.blockers.actions.reject') },
        { kind: 'accept', label: translate('home.blockers.actions.accept') },
      ];
    default:
      return [{ kind: 'resolve', label: translate('home.blockers.actions.resolve') }];
  }
}

export function formatBlockerElapsedLabel(
  createdAt: string,
  translate: (key: string, params?: Record<string, unknown>) => string,
  now = new Date(),
): string {
  const start = new Date(createdAt);
  if (Number.isNaN(start.getTime())) return translate('home.blockers.blockedRecently');

  const diffMs = Math.max(0, now.getTime() - start.getTime());
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (days < 1) return translate('home.blockers.blockedToday');
  if (days < 30) {
    return translate(days === 1 ? 'home.blockers.blockedDay' : 'home.blockers.blockedDays', {
      count: days,
    });
  }

  const months = Math.floor(days / 30);
  if (months < 12) {
    return translate(months === 1 ? 'home.blockers.blockedMonth' : 'home.blockers.blockedMonths', {
      count: months,
    });
  }

  const years = Math.floor(months / 12);
  return translate(years === 1 ? 'home.blockers.blockedYear' : 'home.blockers.blockedYears', {
    count: years,
  });
}

function normalizeBlockerResolveType(resolveType: string): string {
  return resolveType.trim().toLowerCase().replace(/\s+/g, '_');
}

export function formatCycleDateRangeLabel(
  beginDate: string,
  endDate: string,
  locale = DEFAULT_LOCALE,
): string {
  const startLabel = formatDayAndShortMonthLabel(beginDate, locale);
  const endLabel = formatDayAndShortMonthLabel(endDate, locale);
  if (!startLabel || !endLabel) return '';
  return `${startLabel} - ${endLabel}`;
}

export function formatCycleDeliveryDateLabel(
  deliveryDate: string,
  locale = DEFAULT_LOCALE,
): string {
  const parsed = parseLocalDateOnly(deliveryDate);
  if (!parsed) return deliveryDate;

  const formatted = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(parsed);

  return locale.startsWith('es') ? capitalizeSpanishMonthNames(formatted) : formatted;
}

function formatDayAndShortMonthLabel(date: string, locale: string): string | null {
  const parsed = parseLocalDateOnly(date);
  if (!parsed) return null;

  const formatted = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
  }).format(parsed);

  return locale.startsWith('es') ? capitalizeSpanishMonthNames(formatted) : formatted;
}

function capitalizeSpanishMonthNames(label: string): string {
  return label.replace(
    /(\d+\s+|-\s*\d+\s+|\bde\s+)([a-záéíóúñü])/g,
    (_, separator, firstLetter: string) => `${separator}${firstLetter.toUpperCase()}`,
  );
}

function parseLocalDateOnly(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (match) {
    const year = Number(match[1]);
    const month = Number(match[2]) - 1;
    const day = Number(match[3]);
    const date = new Date(year, month, day);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const fallback = new Date(value);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
}

/** Pending (!checkedIn) facility cycles for the home carousel, capped at `limit`. */
export function selectPendingFacilityCycles(
  facilityCycles: readonly FacilityCycle[],
  limit: number,
): readonly FacilityCycle[] {
  return facilityCycles.filter((cycle) => !cycle.checkedIn).slice(0, limit);
}
